/**
 * Animation Cache Utilities
 * Shared caching functionality for animation components
 * Supports version-based cache invalidation for deployments
 */

/**
 * Get current app version for cache versioning
 * @returns {string} Current app version
 */
const getAppVersion = () => {
  // Try environment variable first
  if (process.env.REACT_APP_VERSION) {
    return process.env.REACT_APP_VERSION;
  }

  // Fall back to meta tag
  const versionMeta = document.querySelector('meta[name="app-version"]');
  if (versionMeta) {
    return versionMeta.getAttribute('content') || 'v1';
  }

  // Default fallback
  return 'v1';
};

/**
 * Preload and cache animation images in localStorage
 * @param {string} cacheKey - Base cache key (e.g., 'girl-animation-images')
 * @param {string} imagePath - Path template with {i} placeholder (e.g., '/girl_animation_2/swingT1_{i}.png')
 * @param {number} totalFrames - Total number of animation frames
 * @param {string} version - Cache version for invalidation (defaults to app version)
 * @returns {Promise<Object>} Object with cached images or empty object on failure
 */
export const preloadAndCacheImages = async (
  cacheKey,
  imagePath,
  totalFrames,
  version = null
) => {
  // Use app version if no specific version provided
  const cacheVersion = version || getAppVersion();
  const fullCacheKey = `${cacheKey}-${cacheVersion}`;

  try {
    // Check if images are already cached in localStorage
    const cachedData = localStorage.getItem(fullCacheKey);
    if (cachedData) {
      const parsed = JSON.parse(cachedData);
      console.log(
        `[AnimationCache] ${cacheKey}: Loaded from cache: ${
          Object.keys(parsed).length
        } images`
      );
      return parsed;
    }

    console.log(`[AnimationCache] ${cacheKey}: Cache miss, loading images...`);

    // Preload images and convert to base64 for caching
    const imagePromises = [];
    const imageCache = {};

    for (let i = 0; i < totalFrames; i++) {
      const fullImagePath = imagePath.replace("{i}", i);

      const promise = new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          try {
            // Convert image to base64 for localStorage
            const canvas = document.createElement("canvas");
            const ctx = canvas.getContext("2d");
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);
            const base64 = canvas.toDataURL("image/png");
            imageCache[i] = base64;
            console.log(`[AnimationCache] ${cacheKey}: Cached frame ${i}`);
            resolve();
          } catch (canvasError) {
            console.error(
              `[AnimationCache] ${cacheKey}: Canvas error for frame ${i}:`,
              canvasError
            );
            reject(canvasError);
          }
        };
        img.onerror = (error) => {
          console.error(
            `[AnimationCache] ${cacheKey}: Failed to load frame ${i}:`,
            error
          );
          reject(error);
        };
        img.src = fullImagePath;
      });

      imagePromises.push(promise);
    }

    await Promise.all(imagePromises);

    // Cache images in localStorage
    try {
      localStorage.setItem(fullCacheKey, JSON.stringify(imageCache));
      console.log(
        `[AnimationCache] ${cacheKey}: Successfully cached ${totalFrames} images`
      );
    } catch (storageError) {
      console.error(
        `[AnimationCache] ${cacheKey}: localStorage error:`,
        storageError
      );
      // Continue without caching
    }

    return imageCache;
  } catch (error) {
    console.warn(
      `[AnimationCache] ${cacheKey}: Failed to cache images:`,
      error
    );
    return {};
  }
};

/**
 * Clear animation cache for a specific key
 * @param {string} cacheKey - Base cache key
 * @param {string} version - Cache version (defaults to app version)
 */
export const clearAnimationCache = (cacheKey, version = null) => {
  const cacheVersion = version || getAppVersion();
  const fullCacheKey = `${cacheKey}-${cacheVersion}`;
  try {
    localStorage.removeItem(fullCacheKey);
    console.log(`[AnimationCache] Cleared cache: ${fullCacheKey}`);
  } catch (error) {
    console.error(
      `[AnimationCache] Error clearing cache: ${fullCacheKey}`,
      error
    );
  }
};

/**
 * Get cache size in bytes for a specific key
 * @param {string} cacheKey - Base cache key
 * @param {string} version - Cache version (defaults to app version)
 * @returns {number} Size in bytes
 */
export const getCacheSize = (cacheKey, version = null) => {
  const cacheVersion = version || getAppVersion();
  const fullCacheKey = `${cacheKey}-${cacheVersion}`;
  try {
    const cache = localStorage.getItem(fullCacheKey);
    return cache ? new Blob([cache]).size : 0;
  } catch (error) {
    console.error(
      `[AnimationCache] Error getting cache size: ${fullCacheKey}`,
      error
    );
    return 0;
  }
};

/**
 * Clear all animation caches (for cleanup)
 */
export const clearAllAnimationCaches = () => {
  const keys = Object.keys(localStorage);
  const animationKeys = keys.filter(
    (key) =>
      key.includes("-animation-images-") || key.endsWith("-animation-images")
  );

  animationKeys.forEach((key) => {
    try {
      localStorage.removeItem(key);
      console.log(`[AnimationCache] Cleared: ${key}`);
    } catch (error) {
      console.error(`[AnimationCache] Error clearing: ${key}`, error);
    }
  });

  console.log(
    `[AnimationCache] Cleared ${animationKeys.length} animation caches`
  );
};

/**
 * Get total animation cache usage
 * @returns {Object} Object with total size and breakdown by cache
 */
export const getAnimationCacheUsage = () => {
  const keys = Object.keys(localStorage);
  const animationKeys = keys.filter(
    (key) =>
      key.includes("-animation-images-") || key.endsWith("-animation-images")
  );

  let totalSize = 0;
  const breakdown = {};

  animationKeys.forEach((key) => {
    try {
      const data = localStorage.getItem(key);
      const size = data ? new Blob([data]).size : 0;
      totalSize += size;
      breakdown[key] = size;
    } catch (error) {
      console.error(`[AnimationCache] Error reading: ${key}`, error);
      breakdown[key] = 0;
    }
  });

  return {
    totalSize,
    totalCaches: animationKeys.length,
    breakdown,
  };
};
